import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Calculator, LayoutTemplate, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import type { FinancialScenarioRow, MeResponse, ScenarioTemplateRow } from '@siliconx/shared';
import { EmptyState } from '@siliconx/shared/ui/state';
import { QueryBoundary } from '@siliconx/shared/ui/QueryBoundary';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import { StatusPill } from '@siliconx/shared/ui/StatusPill';
import { api, throwIfNotOk } from '../../../lib/api';
import { formatDate } from '../../../lib/format';
import { qk } from '../../../lib/queryKeys';
import { Button } from '@siliconx/shared/ui/Button';

/**
 * SIL-532 (I2-C2) — `?review=1` is the Overview 4th counter's link target
 * ("to review" = stale ∪ in_review). Reading the union straight off each
 * scenario's already-embedded `staleness`/`status` fields — no second
 * fetch, no server-side filter param, matching the list's existing
 * "no pagination" premise.
 */
const REVIEW_SEARCH = (search: Record<string, unknown>): { review?: boolean } => ({
  review: search.review === '1' || search.review === true || search.review === 'true' ? true : undefined,
});

export const Route = createFileRoute('/financials/scenarios/')({
  component: ScenariosListPage,
  validateSearch: REVIEW_SEARCH,
});

/** True for a scenario the Overview 4th counter's "to review" union covers:
 * stale (SIL-532) OR awaiting approval (`status === 'in_review'`, SIL-568).
 * The SAME predicate the counter itself and this page's `?review=1` filter
 * both apply — one definition, never two. */
function needsReview(s: FinancialScenarioRow): boolean {
  return (s.staleness?.stale ?? false) || s.status === 'in_review';
}

function ScenariosListPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { review } = Route.useSearch();

  // SIL team-ownership — "My Team" / "All Teams" toggle. `null` = the caller
  // hasn't picked yet, so the effective scope falls back to a role-derived
  // default: a teamless org admin has no "mine" to show, so defaults straight
  // to "all"; everyone else (member or admin-with-a-team) defaults to "mine".
  const meQuery = useQuery<MeResponse>({
    queryKey: qk.me(),
    queryFn: async () => {
      const res = await api.api.client.me.$get();
      await throwIfNotOk(res);
      return (await res.json()) as unknown as MeResponse;
    },
  });
  const [teamScopeOverride, setTeamScopeOverride] = useState<'mine' | 'all' | null>(null);
  const defaultTeamScope: 'mine' | 'all' =
    meQuery.data?.role === 'admin' && meQuery.data?.teamId == null ? 'all' : 'mine';
  const teamScope = teamScopeOverride ?? defaultTeamScope;
  // A teamless org admin has no "My Team" to switch to — the toggle only
  // makes sense once the caller actually has a team of their own.
  const showTeamToggle = meQuery.data != null && !(meQuery.data.role === 'admin' && meQuery.data.teamId == null);

  const scenariosQuery = useQuery({
    queryKey: [...qk.scenarios.all, teamScope],
    queryFn: async () => {
      const res = await api.api.client.scenarios.$get({ query: { teamScope } });
      await throwIfNotOk(res);
      return (await res.json()) as unknown as FinancialScenarioRow[];
    },
    // Wait for `me` so a teamless admin never briefly fetches "mine" (which
    // the server would just return empty for) before flipping to "all".
    enabled: meQuery.isSuccess,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.api.client.scenarios[':id'].$delete({ param: { id } });
      await throwIfNotOk(res);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.scenarios.all }),
  });

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <PageHeader
        title="Scenarios"
        pinTo="/financials/scenarios"
        subtitle="What-if fleet planning — hold, sell, ITAD, or refresh cohorts of your portfolio and compare the net position against doing nothing. Saved scenarios are shared across your organization."
        actions={
          <Button
            className="gap-1.5 inline-flex items-center px-3"
            onClick={() => void navigate({ to: '/financials/scenarios/$scenarioId', params: { scenarioId: 'new' }, search: { stage: 1, tab: 'lifecycle' } })}
          >
            <Plus className="size-4" /> New scenario
          </Button>
        }
      />

      <TemplatePicker />

      {showTeamToggle && (
        <div className="flex items-center gap-1.5">
          <TeamScopeButton
            active={teamScope === 'mine'}
            onClick={() => setTeamScopeOverride('mine')}
          >
            My Team
          </TeamScopeButton>
          <TeamScopeButton
            active={teamScope === 'all'}
            onClick={() => setTeamScopeOverride('all')}
          >
            All Teams
          </TeamScopeButton>
        </div>
      )}

      <QueryBoundary
        query={scenariosQuery}
        errorTitle="Couldn't load scenarios."
        emptyState={
          <EmptyState
            icon={Calculator}
            title="No scenarios yet"
            description="Build one to see a net-position comparison."
          />
        }
      >
        {(allScenarios) => {
          const scenarios = review ? allScenarios.filter(needsReview) : allScenarios;
          return scenarios.length === 0 ? (
            <EmptyState
              icon={Calculator}
              title="Nothing to review"
              description="Every scenario is current and none are awaiting your approval."
            />
          ) : (
          <div className="overflow-hidden rounded-xl border border-border bg-card">
            {review && (
              <div className="border-b border-border bg-muted/40 px-5 py-2 text-xs text-muted-foreground">
                Showing scenarios that are stale or awaiting approval.{' '}
                <Link to="/financials/scenarios" search={{}} className="font-medium text-brand-600 hover:underline">
                  Show all
                </Link>
              </div>
            )}
            {scenarios.map((s) => (
              <div
                key={s.id}
                className="flex items-center justify-between border-t border-border px-5 py-3.5 first:border-t-0"
              >
                <Link
                  to="/financials/scenarios/$scenarioId"
                  params={{ scenarioId: s.id }}
                  search={{ stage: 1, tab: 'lifecycle' }}
                  className="min-w-0 flex-1"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-foreground hover:text-brand-700">{s.name}</span>
                    <ScenarioStatusBadge status={s.status} />
                    <StatusPill tone="neutral">{s.teamName ?? 'Unassigned'}</StatusPill>
                    {s.staleness?.stale && (
                      <StatusPill tone="amber" title="A dated assumption has been repriced since this scenario was last saved.">
                        Stale
                      </StatusPill>
                    )}
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {s.horizonMonths}-month horizon · {s.fmvBand.toUpperCase()} band ·{' '}
                    {(s.cohorts ?? []).length} cohort{(s.cohorts ?? []).length === 1 ? '' : 's'} · updated{' '}
                    {formatDate(s.updatedAt)}
                  </div>
                </Link>
                <button
                  type="button"
                  aria-label={`Delete ${s.name}`}
                  onClick={() => {
                    if (confirm(`Delete scenario "${s.name}"? This cannot be undone.`)) {
                      deleteMutation.mutate(s.id);
                    }
                  }}
                  className="ml-4 rounded-md p-1.5 text-muted-foreground hover:bg-red-50 hover:text-red-600"
                >
                  <Trash2 className="size-4" />
                </button>
              </div>
            ))}
          </div>
          );
        }}
      </QueryBoundary>
    </div>
  );
}

/** SIL team-ownership — a small pressed/unpressed toggle button for the
 * "My Team" / "All Teams" scope switch. Not `<Button variant="secondary">`
 * both states at once (that reads as two equally-weighted actions) — one
 * side is always the active filter, so it renders pressed. */
function TeamScopeButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={
        active
          ? 'rounded-md bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-700 ring-1 ring-inset ring-brand-200'
          : 'rounded-md px-2.5 py-1 text-xs font-medium text-muted-foreground hover:bg-muted'
      }
    >
      {children}
    </button>
  );
}

/** SIL-568 status chip — same tone/label mapping `$scenarioId.tsx`'s
 * approval panel already uses, factored here for the list row (draft never
 * shown — the scenario's absence of any chip already reads as draft). */
function ScenarioStatusBadge({ status }: { status: FinancialScenarioRow['status'] }) {
  if (status === 'draft') return null;
  const tone = status === 'approved' ? ('green' as const) : ('amber' as const);
  const label = status === 'approved' ? 'Approved' : 'In review';
  return <StatusPill tone={tone}>{label}</StatusPill>;
}

/**
 * "Start from a template" — instantiation entry point embedded directly in
 * Intelligence > Scenarios (spec §3.1: "no separate templates page"). Org-
 * shared, structure-only templates (SIL-570, I2-C3) created via "Save as
 * template" from a scenario detail page. A zero-match rule still creates
 * the scenario — the response's `zeroMatchCohortLabels` becomes a
 * consequence-first toast naming which cohort(s) matched nothing, rather
 * than a silent success. A rule referencing a location this org can't see
 * is a 400, surfaced as an error toast instead.
 */
function TemplatePicker() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const templatesQuery = useQuery({
    queryKey: qk.scenarioTemplates.all,
    queryFn: async () => {
      const res = await api.api.client['scenario-templates'].$get();
      await throwIfNotOk(res);
      return (await res.json()) as unknown as ScenarioTemplateRow[];
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.api.client['scenario-templates'][':id'].$delete({ param: { id } });
      await throwIfNotOk(res);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.scenarioTemplates.all }),
  });

  const instantiateMutation = useMutation({
    mutationFn: async ({ templateId, name }: { templateId: string; name: string }) => {
      const res = await api.api.client['scenario-templates'][':id'].instantiate.$post({
        param: { id: templateId },
        json: { name },
      });
      await throwIfNotOk(res);
      return (await res.json()) as unknown as {
        scenario: { id: string };
        zeroMatchCohortLabels: string[];
      };
    },
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: qk.scenarios.all });
      if (result.zeroMatchCohortLabels.length > 0) {
        toast.warning(
          `Created — but "${result.zeroMatchCohortLabels.join('", "')}" currently matches no assets. Add a cohort manually or adjust the template.`,
        );
      } else {
        toast.success('Scenario created from template.');
      }
      void navigate({
        to: '/financials/scenarios/$scenarioId',
        params: { scenarioId: result.scenario.id },
        search: { stage: 1, tab: 'lifecycle' },
      });
    },
    onError: (err: Error & { body?: { error?: { message?: string } } }) => {
      toast.error(err.body?.error?.message ?? err.message ?? 'Could not create the scenario from this template.');
    },
  });

  const templates = templatesQuery.data ?? [];
  if (!templatesQuery.isLoading && templates.length === 0) return null;

  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center gap-1.5 text-sm font-medium text-foreground">
        <LayoutTemplate className="size-4" /> Start from a template
      </div>
      <p className="mt-0.5 text-xs text-muted-foreground">
        Org-shared selection rules — assets re-resolve live when you instantiate, never a frozen list.
      </p>
      {templatesQuery.isLoading ? (
        <div className="mt-3 h-10 animate-pulse rounded-md bg-muted" />
      ) : (
        <div className="mt-3 space-y-2">
          {templates.map((t) => (
            <div
              key={t.id}
              className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2"
            >
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-foreground">{t.name}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {t.cohortRules.length} rule{t.cohortRules.length === 1 ? '' : 's'} · {t.horizonMonths}-month
                  horizon · updated {formatDate(t.updatedAt)}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={instantiateMutation.isPending}
                  onClick={() => {
                    const name = window.prompt('Name this scenario', t.name);
                    if (!name) return;
                    instantiateMutation.mutate({ templateId: t.id, name });
                  }}
                >
                  Use template
                </Button>
                <button
                  type="button"
                  aria-label={`Delete template ${t.name}`}
                  onClick={() => {
                    if (confirm(`Delete template "${t.name}"? This cannot be undone.`)) {
                      deleteMutation.mutate(t.id);
                    }
                  }}
                  className="rounded-md p-1.5 text-muted-foreground hover:bg-red-50 hover:text-red-600"
                >
                  <Trash2 className="size-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
